import { useState } from 'react';
import { CaretRightIcon, CircleNotchIcon } from '@phosphor-icons/react';

import classNames from '@/utils/classnames';

import type { ToolCallState } from '../../stores/agent-session-store';

import { ToolCallRow } from './ToolCallRow';

/**
 * Collapsible group of a turn's intermediate steps (tool calls). Keeps the final assistant text —
 * rendered separately in {@link AssistantTurn} — as the focus, tucking the mechanics behind a
 * "Ran N steps" header. Opens by default while the turn is still streaming so live progress is
 * visible, then the caller-controlled state lets the user collapse it.
 */
export function AgentSteps({ toolCalls, streaming }: { toolCalls: ToolCallState[]; streaming: boolean }) {
  const [open, setOpen] = useState(streaming);
  const running = toolCalls.some((tc) => tc.status === 'running');

  if (toolCalls.length === 0) return null;

  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex items-center gap-1.5 self-start rounded-md py-0.5 text-xs font-medium text-fg-tertiary transition-colors hover:text-fg-secondary"
      >
        <CaretRightIcon
          size={12}
          weight="bold"
          className={classNames('shrink-0 transition-transform', open && 'rotate-90')}
        />
        <span className="tabular-nums">
          Ran {toolCalls.length} {toolCalls.length === 1 ? 'step' : 'steps'}
        </span>
        {running && <CircleNotchIcon size={12} className="animate-spin text-fg-tertiary" />}
      </button>
      {open && (
        <div className="flex flex-col gap-2">
          {toolCalls.map((tc) => (
            <ToolCallRow key={tc.id} toolCall={tc} />
          ))}
        </div>
      )}
    </div>
  );
}
